import { useCallback, useEffect, useState } from 'react'
import { api } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Caselle, Vuoto } from '../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Etichetta, Riquadro, Tabella } from '../componenti/base.tsx'
import { giorno, messaggioErrore } from '../componenti/elenco.tsx'

/**
 * Approvazione che accende un interruttore (`attivazione_monitoraggio`), come la riporta
 * GET /amministrazione/impostazioni: quella concessa e ancora utilizzabile, altrimenti quella in
 * attesa, altrimenti l'ultima respinta finche' non scade.
 */
type ApprovazioneAccensione = {
  id: string
  stato: 'in_attesa' | 'approvata' | 'respinta'
  richiesta_il: string
  scade_il: string
  decisa_il: string | null
  nota_decisione: string | null
  richiesta_da: string
  richiedente: string
  favorevoli: number
  necessarie: number
  /** Organi che devono ancora decidere: DIR, DPO (il Referente privacy o il DPO). */
  ruoli_mancanti: string[]
}

type Flag = {
  codice: string; nome: string; descrizione: string; attivo: boolean; prerequisiti: string[]
  attivato_il: string | null
  approvazione: ApprovazioneAccensione | null
}

/** Chi decide l'accensione oggi: la Direzione, piu' il Referente privacy o il DPO se c'e' un incarico attivo. */
type Accensione = { necessarie: number; ruoli_richiesti: string[]; ruoli_mancanti: string[] }

/** Giorni di validita' della richiesta: il massimo del tipo, per lasciare tempo a due decisioni. */
const VALIDITA_RICHIESTA = 30

/**
 * Gli organi a parole. Le regole delle approvazioni dicono «DPO» per l'organo privacy, che e' il
 * Referente privacy o il DPO (499): in CIDIS la Referente ne prende le decisioni.
 */
const ORGANI: Record<string, string> = { DIR: 'la Direzione', DPO: 'il Referente privacy (o il DPO)' }
const organi = (ruoli: string[]) => ruoli.map((r) => ORGANI[r] ?? r).join(' e ')
/** L'organo per cui decide un ruolo: come `organo` di servizi/avvisi.ts nell'API. */
const organo = (ruolo: string) => (ruolo === 'RPR' ? 'DPO' : ruolo)

export function Impostazioni() {
  const { puo } = useSessione()
  return (
    <>
      {puo('consenso.termine') && <TermineFirme />}
      {puo('impostazione.modifica') && <FirmaEmail />}
      {puo('impostazione.leggi') && <ImpostazioniTecniche />}
    </>
  )
}

/**
 * Giorni entro cui caricare il documento che prova il potere di firma di un terzo (456, 458). Il
 * valore vale anche per le firme gia' da completare: cambiarlo sposta il loro termine.
 */
function TermineFirme() {
  const [attuale, setAttuale] = useState<{ giorni: number; aggiornata_il: string | null; aggiornata_da: string | null } | null>(null)
  const [giorni, setGiorni] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  useEffect(() => {
    api.get<NonNullable<typeof attuale>>('/amministrazione/termine-firme')
      .then((r) => { setAttuale(r); setGiorni(String(r.giorni)) })
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [])

  async function salva() {
    setErrore(null); setEsito(null)
    const n = Number(giorni)
    if (!Number.isInteger(n) || n < 1 || n > 365) { setErrore('Scrivi un numero intero di giorni, da 1 a 365.'); return }
    setInCorso(true)
    try {
      await api.put('/amministrazione/termine-firme', { giorni: n })
      setAttuale((a) => a && { ...a, giorni: n })
      setEsito(`Fatto: da ora il documento va caricato entro ${n === 1 ? 'un giorno' : `${n} giorni`}.`)
    } catch (e) { setErrore(messaggioErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <Riquadro titolo="Firme di terzi da completare">
      <p className="piccolo tenue">
        Quando i consensi li firma un genitore, il tutore, l’amministratore di sostegno o il responsabile
        della struttura, il documento che ne prova il potere di firma va caricato entro questo numero di
        giorni. Chi ha registrato la firma riceve un promemoria sette giorni prima; a termine scaduto
        l’avviso arriva anche alla Direzione. Il nuovo valore vale anche per le firme ancora da completare.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      {!attuale && !errore && <Attesa testo="Caricamento…" />}
      {attuale && (
        <div className="riga modulo-in-riga">
          <Campo tipo="number" etichetta="Giorni per caricare il documento" valore={giorni} cambia={setGiorni}
                 min={1} max={365} passi={1}
                 aiuto={attuale.aggiornata_da
                   ? `Ultima modifica di ${attuale.aggiornata_da}, il ${new Date(attuale.aggiornata_il!).toLocaleDateString('it-IT')}.`
                   : undefined} />
          <Bottone variante="primario" inCorso={inCorso} disabled={giorni === String(attuale.giorni)}
                   onClick={() => void salva()}>
            Salva
          </Bottone>
        </div>
      )}
    </Riquadro>
  )
}

/** I testi comuni della firma delle email (508), con la chiave dell'impostazione che li tiene. */
const TESTI_FIRMA = [
  { chiave: 'posta.firma.denominazione', etichetta: 'Denominazione', righe: 1,
    aiuto: 'Sotto il nome di chi scrive.' },
  { chiave: 'posta.firma.sede_legale', etichetta: 'Sede legale', righe: 1,
    aiuto: 'Solo l’indirizzo: la firma lo apre con «Sede legale:».' },
  { chiave: 'posta.firma.nota_ambiente', etichetta: 'Nota sull’ambiente', righe: 2,
    aiuto: 'Dopo i recapiti, separata da una riga vuota.' },
  { chiave: 'posta.firma.riservatezza_it', etichetta: 'Avviso di riservatezza (italiano)', righe: 5,
    aiuto: 'Le virgolette, se servono, fanno parte del testo.' },
  { chiave: 'posta.firma.riservatezza_en', etichetta: 'Avviso di riservatezza (inglese)', righe: 5,
    aiuto: 'In fondo alla firma.' },
] as const

/**
 * Firma delle email (508): i testi comuni a tutte le firme. Un testo vuoto toglie la sua riga; gli a
 * capo dentro un paragrafo diventano spazi, perche' nella firma ogni paragrafo sta su una riga. Nome,
 * sede e telefoni sono di ogni persona; la firma propria di una casella condivisa la decide chi la
 * gestisce. Ogni testo si salva con la rotta delle impostazioni, e resta nel registro.
 */
function FirmaEmail() {
  const [valori, setValori] = useState<Record<string, string> | null>(null)
  const [salvati, setSalvati] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  useEffect(() => {
    api.get<{ impostazioni: { chiave: string; valore: unknown }[] }>('/amministrazione/impostazioni')
      .then((r) => {
        const letti = Object.fromEntries(TESTI_FIRMA.map((t) => {
          const v = r.impostazioni.find((i) => i.chiave === t.chiave)?.valore
          return [t.chiave, typeof v === 'string' ? v : '']
        }))
        setValori(letti); setSalvati(letti)
      })
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [])

  const cambiati = valori ? TESTI_FIRMA.filter((t) => valori[t.chiave] !== salvati[t.chiave]) : []

  async function salva() {
    if (!valori) return
    setErrore(null); setEsito(null); setInCorso(true)
    try {
      for (const t of cambiati) {
        await api.put(`/amministrazione/impostazioni/${t.chiave}`, { valore: valori[t.chiave]!.trim() })
      }
      setSalvati(Object.fromEntries(Object.entries(valori).map(([k, v]) => [k, v.trim()])))
      setValori((v) => v && Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x.trim()])))
      setEsito('Fatto: le firme delle email nuove usano i testi aggiornati.')
    } catch (e) { setErrore(messaggioErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <Riquadro titolo="Firma delle email">
      <p className="piccolo tenue">
        I testi comuni a tutte le firme. Nome, sede principale e telefoni sono di ogni persona (i telefoni li
        scrive lei da «Il mio profilo»); una casella condivisa può avere una firma propria, decisa da chi la
        gestisce. Un testo vuoto toglie la sua riga.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      {!valori && !errore && <Attesa testo="Caricamento…" />}
      {valori && (
        <>
          {TESTI_FIRMA.map((t) => (t.righe === 1
            ? <Campo key={t.chiave} etichetta={t.etichetta} maxLength={2000} aiuto={t.aiuto}
                     valore={valori[t.chiave]!} cambia={(v) => setValori({ ...valori, [t.chiave]: v })} />
            : <AreaTesto key={t.chiave} etichetta={t.etichetta} righe={t.righe} maxLength={2000} aiuto={t.aiuto}
                         valore={valori[t.chiave]!} cambia={(v) => setValori({ ...valori, [t.chiave]: v })} />))}
          <Bottone variante="primario" inCorso={inCorso} disabled={!cambiati.length} onClick={() => void salva()}>
            Salva i testi della firma
          </Bottone>
        </>
      )}
    </Riquadro>
  )
}

function ImpostazioniTecniche() {
  const [dati, setDati] = useState<{
    impostazioni: { chiave: string; valore: unknown; descrizione: string; sensibile: boolean }[]
    flag: Flag[]
    attivazione: Accensione
  } | null>(null)
  const [errore, setErrore] = useState<string | null>(null)

  const carica = useCallback(() => {
    api.get<NonNullable<typeof dati>>('/amministrazione/impostazioni')
      .then(setDati).catch((e) => setErrore(messaggioErrore(e)))
  }, [])
  useEffect(carica, [carica])

  if (!dati) return errore ? <Avviso tipo="errore">{errore}</Avviso> : <Attesa testo="Caricamento…" />

  return (
    <>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      <Avviso tipo="attenzione">
        L’attivazione delle funzioni di sorveglianza richiede l’accordo sindacale o l’autorizzazione
        dell’Ispettorato del lavoro previsti dall’articolo 4 della legge 300/1970 e l’approvazione
        della Direzione e, se designato, del Referente privacy (o del DPO).
      </Avviso>

      {dati.flag.map((f) => (
        <Interruttore key={f.codice} flag={f} accensione={dati.attivazione} cambiato={carica} />
      ))}

      <Riquadro titolo="Impostazioni" denso>
        <Tabella intestazioni={['Chiave', 'Valore', 'Descrizione']}
                 vuoto={<Vuoto testo="Nessuna impostazione." />}>
          {dati.impostazioni.map((i) => (
            <tr key={i.chiave}>
              <td data-etichetta="Chiave" className="mono piccolo">{i.chiave}</td>
              <td data-etichetta="Valore" className="mono">{JSON.stringify(i.valore)}</td>
              <td data-etichetta="Descrizione" className="piccolo tenue">
                {i.descrizione}
                {i.sensibile && <> <Etichetta tono="attenzione">doppia approvazione</Etichetta></>}
              </td>
            </tr>
          ))}
        </Tabella>
      </Riquadro>
    </>
  )
}

/**
 * Un interruttore con il suo percorso di accensione (adeguamenti 1 e 2 del LEGGIMI della webmail,
 * docs/legale/webmail/).
 *
 * Spento: chi puo' chiedere approvazioni chiede `attivazione_monitoraggio` con una motivazione che
 * riporta gli estremi degli atti; Direzione e Referente privacy (o DPO) decidono in «Approvazioni»; concessa, chi ha
 * `flag.gestisci` spunta i prerequisiti e preme «Attiva». La pagina trasmette l'approvazione e le
 * voci spuntate; il server rifiuta se manca l'una o l'altra, e spende l'approvazione all'accensione.
 * Lo spegnimento non chiede approvazione: togliere un controllo non lo rende piu' invasivo.
 */
function Interruttore({ flag: f, accensione, cambiato }: {
  flag: Flag; accensione: Accensione; cambiato: () => void
}) {
  const { io, puo } = useSessione()
  const [spunte, setSpunte] = useState<string[]>([])
  const [chiedendo, setChiedendo] = useState(false)
  const [motivazione, setMotivazione] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)

  const gestisce = puo('flag.gestisci')
  const a = f.approvazione
  const concessa = a?.stato === 'approvata' ? a : null
  const tutte = f.prerequisiti.every((p) => spunte.includes(p))
  const puoChiedere = puo('approvazione.richiedi') && !accensione.ruoli_mancanti.length
    && (!a || a.stato === 'respinta')
  // Chi decide l'accensione e la chiede lui stesso non vale come decisore: con un solo DPO o un solo
  // Referente privacy la richiesta resterebbe ferma, e l'API non la apre. Lo si dice prima di
  // inviarla. Il Referente privacy decide per l'organo «DPO» (499).
  const decideIo = [...new Set((io?.incarichi ?? []).map((i) => organo(i.ruolo)))]
    .filter((r) => accensione.ruoli_richiesti.includes(r))

  async function esegui(azione: () => Promise<unknown>, fatto: string) {
    setErrore(null); setEsito(null); setInCorso(true)
    try {
      await azione()
      setEsito(fatto)
      cambiato()
    } catch (e) { setErrore(messaggioErrore(e)) } finally { setInCorso(false) }
  }

  const chiedi = () => esegui(async () => {
    await api.post('/approvazioni', {
      tipo: 'attivazione_monitoraggio', oggetto_tipo: 'flag', oggetto_id: f.codice,
      motivazione: motivazione.trim(), giorni_validita: VALIDITA_RICHIESTA,
    })
    setChiedendo(false)
    setMotivazione('')
  }, 'Richiesta inviata: si decide nella pagina «Approvazioni».')

  const accendi = () => esegui(() => api.put(`/amministrazione/flag/${f.codice}`, {
    attivo: true, approvazione_id: concessa?.id, prerequisiti_confermati: spunte,
  }), 'Funzione attivata.')

  const spegni = () => esegui(
    () => api.put(`/amministrazione/flag/${f.codice}`, { attivo: false }), 'Funzione disattivata.')

  return (
    <Riquadro titolo={f.nome}
      azioni={
        <Etichetta tono={f.attivo ? 'positivo' : 'neutro'}>
          {f.attivo ? 'attiva' : 'non attiva'}
        </Etichetta>
      }>
      <p className="piccolo tenue">{f.descrizione}</p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}

      {f.attivo ? (
        <>
          {f.attivato_il && <p className="piccolo tenue">Attiva dal {giorno(f.attivato_il)}.</p>}
          {gestisce && (
            <Bottone variante="secondario" inCorso={inCorso} onClick={() => void spegni()}>
              Disattiva
            </Bottone>
          )}
        </>
      ) : (
        <>
          <StatoApprovazione approvazione={a} accensione={accensione} />

          {puoChiedere && !chiedendo && (
            <div className="riga">
              <Bottone variante="secondario" onClick={() => { setEsito(null); setChiedendo(true) }}>
                {a ? 'Chiedi di nuovo l’approvazione' : 'Chiedi l’approvazione'}
              </Bottone>
            </div>
          )}
          {chiedendo && (
            <form onSubmit={(e) => { e.preventDefault(); void chiedi() }}>
              {decideIo.length > 0 && (
                <Avviso tipo="attenzione">
                  Questa richiesta la decidi anche tu, come {organi(decideIo)}. Se la chiedi tu, la
                  tua decisione non vale e serve un’altra persona con lo stesso ruolo. Di norma la
                  chiede chi amministra il gestionale.
                </Avviso>
              )}
              <AreaTesto etichetta="Motivazione" obbligatorio righe={4} valore={motivazione}
                         cambia={setMotivazione} maxLength={1000}
                         aiuto="Gli estremi di ogni presupposto: data, protocollo, dove è archiviato. La leggono Direzione e Referente privacy." />
              <p className="piccolo tenue">
                La richiesta vale {VALIDITA_RICHIESTA} giorni: entro quel termine va decisa e usata.
              </p>
              <div className="azioni-modulo">
                <Bottone variante="discreto" onClick={() => setChiedendo(false)}>Annulla</Bottone>
                <Bottone tipo="submit" variante="primario" inCorso={inCorso}
                         disabled={motivazione.trim().length < 10}>
                  Invia la richiesta
                </Bottone>
              </div>
            </form>
          )}

          {f.prerequisiti.length > 0 && (gestisce ? (
            <fieldset className="gruppo-caselle">
              <legend>Prerequisiti da verificare</legend>
              <Caselle voci={f.prerequisiti.map((p) => ({ codice: p, nome: p }))}
                       scelte={spunte} cambia={setSpunte} />
            </fieldset>
          ) : (
            <p className="piccolo tenue">Prerequisiti: {f.prerequisiti.join('; ')}.</p>
          ))}

          {gestisce && (
            <>
              <Bottone variante="primario" inCorso={inCorso} disabled={!concessa || !tutte}
                       onClick={() => void accendi()}>
                Attiva
              </Bottone>
              {(!concessa || !tutte) && (
                <p className="piccolo tenue">
                  «Attiva» si abilita con l’approvazione concessa e tutti i prerequisiti spuntati.
                </p>
              )}
            </>
          )}
        </>
      )}
    </Riquadro>
  )
}

/** A che punto e' l'approvazione di accensione, detto a parole. */
function StatoApprovazione({ approvazione: a, accensione }: {
  approvazione: ApprovazioneAccensione | null; accensione: Accensione
}) {
  if (!a) {
    const chi = accensione.ruoli_richiesti.length ? accensione.ruoli_richiesti : ['DIR']
    return (
      <>
        <p className="piccolo">
          Per accenderla {chi.length > 1 ? 'devono' : 'deve'} approvarla {organi(chi)}.
        </p>
        {accensione.ruoli_mancanti.length > 0 && (
          <Avviso tipo="attenzione">
            Nessun incarico attivo porta {organi(accensione.ruoli_mancanti)}: finché non è designato
            la richiesta non si può aprire.
          </Avviso>
        )}
      </>
    )
  }
  if (a.stato === 'in_attesa') {
    return (
      <p className="piccolo">
        <Etichetta tono="attenzione">in attesa</Etichetta>{' '}
        Chiesta da {a.richiedente} il {giorno(a.richiesta_il)}. Decisioni favorevoli: {a.favorevoli} di{' '}
        {a.necessarie}.
        {a.ruoli_mancanti.length > 0 && (
          <> {a.ruoli_mancanti.length > 1 ? 'Devono' : 'Deve'} ancora decidere {organi(a.ruoli_mancanti)}.</>
        )}{' '}
        Si decide nella pagina «Approvazioni».
      </p>
    )
  }
  if (a.stato === 'approvata') {
    return (
      <p className="piccolo">
        <Etichetta tono="positivo">approvata</Etichetta>{' '}
        Approvazione concessa il {giorno(a.decisa_il)}, valida fino al {giorno(a.scade_il)}.
      </p>
    )
  }
  return (
    <p className="piccolo">
      <Etichetta tono="critico">respinta</Etichetta>{' '}
      Richiesta respinta il {giorno(a.decisa_il)}{a.nota_decisione ? `: «${a.nota_decisione}»` : ''}.
    </p>
  )
}
